import * as Sentry from "@sentry/react";

type ErrorLike = {
  originalError?: Error;
  error?: Error;
};

type SentryDebugContext = Record<string, unknown>;

const TERMINAL_VERIFICATION_REASONS = new Set([
  "Verification has been completed",
  "Verification has been cancelled",
]);

const SENTRY_DEBUG_CONTEXT_KEY = Symbol("sentryDebugContext");

type ErrorWithSentryDebugContext = Error & {
  [SENTRY_DEBUG_CONTEXT_KEY]?: SentryDebugContext;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getErrorReason = (error: unknown): string | undefined => {
  if (!isRecord(error) || typeof error.reason !== "string") {
    return undefined;
  }

  return error.reason;
};

const serializeError = (error: unknown): string => {
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const toErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (Array.isArray(error)) {
    const messages = error.filter(
      (item): item is string => typeof item === "string",
    );
    return messages.length ? messages.join(", ") : serializeError(error);
  }

  if (isRecord(error)) {
    if (typeof error.message === "string" && error.message.trim()) {
      return error.message;
    }

    const firstStringArray = Object.values(error).find(
      (value): value is string[] =>
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((item) => typeof item === "string"),
    );

    if (firstStringArray?.length) {
      return firstStringArray.join(", ");
    }

    return serializeError(error);
  }

  return String(error);
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) return error;

  const normalizedError = new Error(toErrorMessage(error));
  if (isRecord(error) && typeof error.name === "string" && error.name.trim()) {
    normalizedError.name = error.name;
  }
  return normalizedError;
};

const containsTerminalVerificationReason = (value: string): boolean =>
  Array.from(TERMINAL_VERIFICATION_REASONS).some((reason) =>
    value.includes(reason),
  );

const shouldSuppressExpectedVerificationError = (
  error: unknown,
  normalizedError: Error,
): boolean => {
  const reason = getErrorReason(error);
  if (reason && TERMINAL_VERIFICATION_REASONS.has(reason)) {
    return true;
  }

  return containsTerminalVerificationReason(normalizedError.message);
};

const getAttachedSentryContext = (
  error: unknown,
): SentryDebugContext | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }

  const sentryDebugContext = (error as ErrorWithSentryDebugContext)[
    SENTRY_DEBUG_CONTEXT_KEY
  ];

  return isRecord(sentryDebugContext) ? sentryDebugContext : undefined;
};

const mergeSentryContexts = (
  ...contexts: Array<SentryDebugContext | undefined>
): SentryDebugContext | undefined => {
  const mergedContext = contexts.reduce<SentryDebugContext>((acc, context) => {
    if (!context) {
      return acc;
    }

    return {
      ...acc,
      ...context,
    };
  }, {});

  return Object.keys(mergedContext).length ? mergedContext : undefined;
};

export const attachSentryContext = <T extends Error>(
  error: T,
  sentryContext: SentryDebugContext,
): T => {
  const nextContext = mergeSentryContexts(
    getAttachedSentryContext(error),
    sentryContext,
  );

  if (!nextContext) {
    return error;
  }

  Object.defineProperty(error, SENTRY_DEBUG_CONTEXT_KEY, {
    value: nextContext,
    configurable: true,
    writable: true,
  });

  return error;
};

const captureException = (
  error: unknown,
  sentryContext?: SentryDebugContext,
) => {
  const errorLike = error as ErrorLike;
  const normalizedError =
    errorLike.originalError ?? errorLike.error ?? toError(error);

  if (shouldSuppressExpectedVerificationError(error, normalizedError)) {
    return;
  }

  if (import.meta.env.VITE_SENTRY_DSN) {
    const debugContext = mergeSentryContexts(
      getAttachedSentryContext(normalizedError),
      sentryContext,
    );
    const shouldCaptureRawError =
      Array.isArray(error) || (isRecord(error) && !(error instanceof Error));

    if (shouldCaptureRawError || debugContext) {
      Sentry.withScope((scope) => {
        if (shouldCaptureRawError) {
          scope.setExtra("raw_error", serializeError(error));
        }
        if (debugContext) {
          scope.setContext("error_debug", debugContext);
        }
        Sentry.captureException(normalizedError);
      });
      return;
    }

    Sentry.captureException(normalizedError);
  } else {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};
export default captureException;
